/**
 * Analytics Service for Aprendos Web Platform
 *
 * Provides a centralized interface for tracking user events.
 * Uses Firebase Analytics under the hood.
 */

import { app } from '../firebase';
import { hasAnalyticsConsent } from '../lib/consent';

let analyticsPromise = null;

async function getConsentAwareAnalytics() {
  if (!hasAnalyticsConsent()) {
    return null;
  }

  if (!analyticsPromise) {
    analyticsPromise = import('firebase/analytics')
      .then(({ getAnalytics, isSupported }) =>
        isSupported().then((supported) => (supported ? getAnalytics(app) : null))
      )
      .catch((error) => {
        if (import.meta.env.DEV) {
          console.warn('Firebase Analytics unavailable:', error?.message || error);
        }
        return null;
      });
  }

  return analyticsPromise;
}

function sendAnalyticsEvent(eventName, params = {}) {
  void getConsentAwareAnalytics().then(async (analytics) => {
    if (analytics) {
      const { logEvent } = await import('firebase/analytics');
      logEvent(analytics, eventName, cleanAnalyticsParams(params));
    }
  });
}

function cleanAnalyticsParams(params) {
  return Object.fromEntries(
    Object.entries(params).filter(([, value]) =>
      value !== undefined && value !== null && value !== ''
    )
  );
}

function funnelContext(context = {}) {
  return {
    source: context.source,
    landing_page: context.landingPage,
    language: context.language,
  };
}

export function trackPrimaryCtaClicked(context = {}) {
  sendAnalyticsEvent('primary_cta_clicked', {
    ...funnelContext(context),
    cta_location: context.ctaLocation,
  });
}

export function trackDemoStarted(context = {}) {
  sendAnalyticsEvent('demo_started', {
    ...funnelContext(context),
    demo_id: context.demoId,
  });
}

export function trackDemoProgress(progressPercentage, context = {}) {
  sendAnalyticsEvent('demo_progress', {
    ...funnelContext(context),
    demo_id: context.demoId,
    progress_percentage: progressPercentage,
  });
}

export function trackDemoCompleted(context = {}) {
  sendAnalyticsEvent('demo_completed', {
    ...funnelContext(context),
    demo_id: context.demoId,
  });
}

export function trackSignupViewed(context = {}) {
  sendAnalyticsEvent('signup_viewed', funnelContext(context));
}

export function trackSignupMethodSelected(method, context = {}) {
  sendAnalyticsEvent('signup_method_selected', {
    ...funnelContext(context),
    signup_method: method,
  });
}

export function trackSignupStarted(method, context = {}) {
  sendAnalyticsEvent('signup_started', {
    ...funnelContext(context),
    signup_method: method,
  });
}

export function trackEmailVerificationRequired(method = 'email') {
  sendAnalyticsEvent('email_verification_required', { signup_method: method });
}

export function trackEmailVerified(method = 'email') {
  sendAnalyticsEvent('email_verified', { signup_method: method });
}

export function trackOnboardingStepViewed(step, context = {}) {
  sendAnalyticsEvent('onboarding_step_viewed', {
    ...funnelContext(context),
    onboarding_step: step,
  });
}

export function trackOnboardingStepCompleted(step, context = {}) {
  sendAnalyticsEvent('onboarding_step_completed', {
    ...funnelContext(context),
    onboarding_step: step,
    choice: context.choice,
    level: context.level,
  });
}

export function trackOnboardingCompleted(language, level, levelSource) {
  sendAnalyticsEvent('onboarding_completed', {
    language,
    level,
    level_source: levelSource,
  });
}

/**
 * Track when a user signs up
 * @param {string} method - Sign-up method (e.g., 'google', 'email')
 */
export function trackSignUp(method = 'email') {
  sendAnalyticsEvent('sign_up', {
    method,
    signup_method: method,
  });
}

/**
 * Track when a user logs in
 * @param {string} method - Login method (e.g., 'google', 'email')
 */
export function trackLogin(method = 'email') {
  sendAnalyticsEvent('login', { method });
}

/**
 * Track placement test completion
 * @param {string} language - Language tested (e.g., 'es', 'en')
 * @param {string} level - Assigned level (e.g., 'A2', 'B1')
 * @param {number} score - Test score percentage
 */
export function trackPlacementTestCompleted(language, level, score) {
  sendAnalyticsEvent('placement_test_completed', {
    language,
    assigned_level: level,
    score
  });
}

/**
 * Track when a lesson/episode is started
 * @param {string} lessonId - Unique lesson identifier
 * @param {string} level - CEFR level (A1-C2)
 * @param {string} language - Target language
 */
export function trackLessonStarted(lessonId, level, language) {
  sendAnalyticsEvent('lesson_started', {
    lesson_id: lessonId,
    level,
    language
  });
}

/**
 * Track when a lesson/episode is completed
 * @param {string} lessonId - Unique lesson identifier
 * @param {string} level - CEFR level (A1-C2)
 * @param {string} language - Target language
 * @param {number} durationSeconds - Time spent on lesson
 */
export function trackLessonCompleted(lessonId, level, language, durationSeconds) {
  sendAnalyticsEvent('lesson_completed', {
    lesson_id: lessonId,
    level,
    language,
    duration_seconds: durationSeconds
  });
}

/**
 * Track exercise completion
 * @param {string} exerciseType - Type of exercise (e.g., 'vocabulary', 'grammar', 'listening')
 * @param {string} level - CEFR level
 * @param {boolean} correct - Whether the answer was correct
 */
export function trackExerciseCompleted(exerciseType, level, correct, context = {}) {
  sendAnalyticsEvent('exercise_completed', {
    exercise_type: exerciseType,
    level,
    correct,
    lesson_id: context.lessonId,
    language: context.language,
    score: context.score,
    question_count: context.questionCount,
  });
}

/**
 * Track spaced repetition review session
 * @param {string} language - Target language
 * @param {number} cardsReviewed - Number of cards reviewed
 * @param {number} correctCount - Number of correct answers
 */
export function trackSRSReviewCompleted(language, cardsReviewed, correctCount) {
  sendAnalyticsEvent('srs_review_completed', {
    language,
    cards_reviewed: cardsReviewed,
    correct_count: correctCount,
    accuracy: cardsReviewed > 0 ? Math.round((correctCount / cardsReviewed) * 100) : 0
  });
}

/**
 * Track streak milestone
 * @param {number} streakDays - Current streak length
 */
export function trackStreakMilestone(streakDays) {
  sendAnalyticsEvent('streak_milestone', {
    streak_days: streakDays
  });
}

/**
 * Track screen/page view
 * @param {string} screenName - Name of the screen
 */
export function trackScreenView(screenName) {
  sendAnalyticsEvent('screen_view', {
    screen_name: screenName
  });
}

/**
 * Track when a graded reader is started
 * @param {string} seriesId - Series identifier
 * @param {string} storyId - Story identifier
 * @param {string} level - CEFR level
 * @param {string} language - Target language
 */
export function trackReaderStarted(seriesId, storyId, level, language) {
  sendAnalyticsEvent('reader_started', {
    series_id: seriesId,
    story_id: storyId,
    level,
    language
  });
}

/**
 * Track when a graded reader is completed
 * @param {string} seriesId - Series identifier
 * @param {string} storyId - Story identifier
 * @param {string} level - CEFR level
 * @param {string} language - Target language
 * @param {number} readingTimeSeconds - Time spent reading
 */
export function trackReaderCompleted(seriesId, storyId, level, language, readingTimeSeconds) {
  sendAnalyticsEvent('reader_completed', {
    series_id: seriesId,
    story_id: storyId,
    level,
    language,
    reading_time_seconds: readingTimeSeconds
  });
}

/**
 * Track reading progress milestone
 * @param {string} seriesId - Series identifier
 * @param {string} storyId - Story identifier
 * @param {number} progressPercentage - Progress percentage (25, 50, 75, 100)
 */
export function trackReaderProgress(seriesId, storyId, progressPercentage) {
  sendAnalyticsEvent('reader_progress', {
    series_id: seriesId,
    story_id: storyId,
    progress_percentage: progressPercentage
  });
}

/**
 * Set the user ID for analytics (call after authentication)
 * @param {string} userId - Firebase user UID
 */
export function setAnalyticsUserId(userId) {
  void getConsentAwareAnalytics().then(async (analytics) => {
    if (analytics) {
      const { setUserId } = await import('firebase/analytics');
      setUserId(analytics, userId);
    }
  });
}

/**
 * Set user properties for segmentation
 * @param {Object} properties - User properties object
 * @param {string} [properties.subscription_tier] - Free, premium, etc.
 * @param {string} [properties.target_language] - Primary learning language
 * @param {string} [properties.current_level] - Current CEFR level
 * @param {string} [properties.platform] - 'web'
 */
export function setAnalyticsUserProperties(properties) {
  void getConsentAwareAnalytics().then(async (analytics) => {
    if (analytics) {
      const { setUserProperties } = await import('firebase/analytics');
      setUserProperties(analytics, {
        ...properties,
        platform: 'web'
      });
    }
  });
}

/**
 * Generic event tracking for custom events
 * @param {string} eventName - Event name (snake_case)
 * @param {Object} params - Event parameters
 */
export function trackEvent(eventName, params = {}) {
  sendAnalyticsEvent(eventName, params);
}

// =============================================================================
// Voice Conversation Analytics
// =============================================================================

/**
 * Track when a user clicks a voice entry point
 * @param {string} entryPoint - Entry point identifier (1-4)
 * @param {string} episodeId - Episode ID
 */
export function trackVoiceEntryClicked(entryPoint, episodeId) {
  sendAnalyticsEvent('voice_entry_clicked', {
    entry_point: entryPoint,
    episode_id: episodeId
  });
}

/**
 * Track when a voice session starts
 * @param {string} episodeId - Episode ID
 * @param {string} personaId - Selected persona ID
 * @param {string} tier - User subscription tier
 */
export function trackVoiceSessionStarted(episodeId, personaId, tier) {
  sendAnalyticsEvent('voice_session_started', {
    episode_id: episodeId,
    persona_id: personaId,
    tier
  });
}

/**
 * Track when a voice session ends
 * @param {number} durationSeconds - Total session duration in seconds
 * @param {string} endReason - Reason for ending (user_ended, episode_cap, wallet_empty, error)
 * @param {string} episodeId - Episode ID
 */
export function trackVoiceSessionEnded(durationSeconds, endReason, episodeId) {
  sendAnalyticsEvent('voice_session_ended', {
    duration_seconds: durationSeconds,
    end_reason: endReason,
    episode_id: episodeId
  });
}

/**
 * Track voice minutes consumed
 * @param {number} deltaSeconds - Seconds consumed in this event
 * @param {string} sourceGrantType - Grant type (beta_grant, subscription, purchase)
 */
export function trackVoiceMinutesConsumed(deltaSeconds, sourceGrantType) {
  sendAnalyticsEvent('voice_minutes_consumed', {
    delta_seconds: deltaSeconds,
    source_grant_type: sourceGrantType
  });
}

/**
 * Track when episode cap is reached
 * @param {string} episodeId - Episode ID
 */
export function trackVoiceEpisodeCapReached(episodeId) {
  sendAnalyticsEvent('voice_episode_cap_reached', {
    episode_id: episodeId
  });
}

/**
 * Track when wallet is exhausted
 * @param {string} lastEpisodeId - Last episode being used
 */
export function trackVoiceWalletExhausted(lastEpisodeId) {
  sendAnalyticsEvent('voice_wallet_exhausted', {
    last_episode_id: lastEpisodeId
  });
}

/**
 * Track when a refund is issued
 * @param {number} refundSeconds - Seconds refunded
 * @param {string} reason - Reason for refund (crash, disconnect, error)
 */
export function trackVoiceRefundIssued(refundSeconds, reason) {
  sendAnalyticsEvent('voice_refund_issued', {
    refund_seconds: refundSeconds,
    reason
  });
}

/**
 * Track persona selection
 * @param {string} personaId - Selected persona ID
 * @param {string} episodeId - Episode ID
 */
export function trackVoicePersonaSelected(personaId, episodeId) {
  sendAnalyticsEvent('voice_persona_selected', {
    persona_id: personaId,
    episode_id: episodeId
  });
}

export { getConsentAwareAnalytics };
